<template>
  <div class="flex flex-col w-full overflow-y-scroll h-full">
    <div
      v-if="loading"
      class="flex flex-col w-full justify-center items-center h-[90vh]">
      <loader />
    </div>
    <div v-else-if="submissions.length" class="flex flex-col gap-3 w-full h-full">
      <div
        v-for="sub in submissions"
        :key="sub._id"
        class="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-md cursor-pointer hover:border-gray-300 transition-colors shadow-sm"
        @click="openSubmission(sub)"
      >
        <div class="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-none">
          <i class="pi pi-file text-primary text-lg"></i>
        </div>
        <div class="flex flex-col flex-1 min-w-0">
          <span class="text-sm font-medium truncate">{{ sub.formTitle }}</span>
          <span class="text-xs text-gray-500">{{ formatDate(sub.submittedAt) }}</span>
        </div>
        <img
          v-if="sub.signatureDataUrl"
          :src="sub.signatureDataUrl"
          class="w-12 h-8 object-contain border border-gray-100 rounded flex-none"
          alt="signature"
        />
        <i class="pi pi-chevron-left text-gray-400 text-sm flex-none"></i>
      </div>
    </div>
    <div v-else class="w-full h-full flex flex-col justify-start items-center my-auto gap-2 pt-8">
      <i class="pi pi-file text-4xl text-gray-300"></i>
      <span class="text-sm text-gray-500">אין טפסים חתומים</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, type PropType } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import type { ICustomer, IConsentFormSubmission } from '@torim-app/types';
import { ConsentFormsApi } from '@app/api/admin/consent-forms-api';
import ConsentFormSubmissionView from './ConsentFormSubmissionView.vue';

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true,
  },
});

const toast = useToast();
const { openDrawer, closeDrawer } = useDrawer();

const loading = ref(true);
const submissions = ref<IConsentFormSubmission[]>([]);

const formatDate = (date: Date | string) => {
  return DateTime.fromISO(String(date)).toLocaleString(DateTime.DATETIME_MED);
};

const openSubmission = (sub: IConsentFormSubmission) => {
  openDrawer(ConsentFormSubmissionView, {
    submission: sub,
    onClose: closeDrawer,
  }, {
    title: sub.formTitle,
    closable: true,
    noPadding: true,
  });
};

const loadSubmissions = async () => {
  loading.value = true;
  try {
    submissions.value = await ConsentFormsApi.fetchCustomerSubmissions(props.customer.id);
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון את הטפסים');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadSubmissions();
});
</script>
